export const moneyFormatter = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
});

export const formatMoney = (value) => {
  if (value === null || value === undefined) {
    return moneyFormatter.format(0);
  }
  return moneyFormatter.format(value);
};

export const formatNumericInput = (rawValue) => {
  const source = String(rawValue ?? "");
  const cleaned = source.replace(/[^\d,]/g, "");
  if (!cleaned) {
    return "";
  }

  const commaIndex = cleaned.indexOf(",");
  const hasDecimal = commaIndex !== -1;
  const integerDigits = (hasDecimal ? cleaned.slice(0, commaIndex) : cleaned).replace(/^0+(?=\d)/, "");
  const decimalDigits = hasDecimal ? cleaned.slice(commaIndex + 1).replace(/\D/g, "").slice(0, 2) : "";
  const groupedInteger = integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  if (!hasDecimal) {
    return groupedInteger;
  }
  return decimalDigits ? `${groupedInteger},${decimalDigits}` : `${groupedInteger},`;
};

export const parseNumericInput = (formattedValue) => {
  if (formattedValue === "" || formattedValue === null || formattedValue === undefined) {
    return null;
  }
  const normalized = String(formattedValue).replace(/\./g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatNumberToInput = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return "";
  }
  const [integerPart, decimalPart] = parsed.toFixed(2).split(".");
  const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  if (decimalPart === "00") {
    return groupedInteger;
  }
  return `${groupedInteger},${decimalPart.replace(/0+$/, "")}`;
};

export const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const allocateCondonationByPriority = (amount, orderedComponents) => {
  let remaining = roundMoney(Math.max(0, amount));
  const allocations = {};

  orderedComponents.forEach((item) => {
    if (!item.enabled) {
      allocations[item.component] = 0;
      return;
    }
    const expected = roundMoney(Math.max(0, item.expected || 0));
    const allocated = roundMoney(Math.min(remaining, expected));
    allocations[item.component] = allocated;
    remaining = roundMoney(remaining - allocated);
  });

  return { allocations, remaining };
};

const HIERARCHY_EPSILON = 0.01;

export const isComponentFullyCondoned = (allocated, expected) => {
  const expectedAmount = roundMoney(Math.max(0, expected || 0));
  if (expectedAmount <= HIERARCHY_EPSILON) {
    return true;
  }
  return roundMoney(allocated || 0) + HIERARCHY_EPSILON >= expectedAmount;
};

export const getHierarchyBlockers = (component, orderedComponents, amountsByComponent) => {
  const blockers = [];
  for (let index = 0; index < orderedComponents.length; index += 1) {
    const item = orderedComponents[index];
    if (item.component === component) {
      break;
    }
    if (item.enabled === false) {
      continue;
    }
    if (!isComponentFullyCondoned(amountsByComponent[item.component], item.expected)) {
      blockers.push(item);
    }
  }
  return blockers;
};

export const getLaterComponents = (component, orderedComponents) => {
  const later = [];
  let seen = false;
  orderedComponents.forEach((item) => {
    if (item.component === component) {
      seen = true;
      return;
    }
    if (seen) {
      later.push(item);
    }
  });
  return later;
};

export const findHierarchyViolation = (orderedComponents, amountsByComponent) => {
  const pendingBlockers = [];
  for (let index = 0; index < orderedComponents.length; index += 1) {
    const item = orderedComponents[index];
    if (item.enabled === false) {
      continue;
    }
    const allocated = roundMoney(amountsByComponent[item.component] || 0);
    if (allocated > HIERARCHY_EPSILON && pendingBlockers.length > 0) {
      return {
        skipped: item,
        blockers: pendingBlockers,
      };
    }
    if (!isComponentFullyCondoned(allocated, item.expected)) {
      pendingBlockers.push(item);
    }
  }
  return null;
};

export const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  const [datePart] = String(value).split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) {
    return "-";
  }
  return new Date(year, month - 1, day).toLocaleDateString("es-CO");
};

export const formatCreditOldId = (credit) => {
  const value = credit?.oldId ?? credit?.oldID ?? credit?.old_id;
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return String(value);
};

export const CREDIT_STATUS_LABELS = {
  APPROVED: "Aprobado",
  COMPLETED: "Completado",
  IN_ARREARS: "En mora",
  CANCELLED: "Cancelado",
  REESTRUCTURADO: "Reestructurado",
  PRE_APPROVED: "Preaprobado",
};

export const QUOTA_STATUS_LABELS = {
  APPROVED: "Aprobada",
  PAID: "Pagada",
  PENDING: "Pendiente",
  PARTIALLY_PAID: "Pago parcial",
  OVERDUE: "En mora",
  IN_ARREARS: "En mora",
  REESTRUCTURADA: "Reestructurada",
  AMPLIADA: "Ampliada",
  CANCELLED: "Cancelada",
  CONDONED: "Condonada",
  CONDONADA: "Condonada",
};

/** Quota statuses that still admit a condonation (mirror of the backend `activeQuotaStatuses`). */
export const ACTIVE_QUOTA_STATUSES = ["PENDING", "PARTIALLY_PAID", "OVERDUE"];

/**
 * Una cuota es "vencida" (condonable) solo si está PENDING y su payDate es hoy o anterior. Espeja la
 * regla del backend (isOverdue): OVERDUE no se persiste; el vencido se calcula por fecha. Recibe el
 * objeto `quota` (QuotaThinDto con `status` y `payDate`).
 */
export const isQuotaOverdue = (quota) => {
  if (!quota || quota.status !== "PENDING" || !quota.payDate) {
    return false;
  }
  const payDate = new Date(String(quota.payDate).split("T")[0]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return payDate <= today;
};

export const COMPONENT_LABELS = {
  GAC: "GAC",
  MORA: "Mora",
  INTERES_CORRIENTE: "Interés corriente",
  FGA: "FGA",
  SEGURO_VIDA: "Seguro de vida",
  SEGURO_DESEMPLEO: "Seguro de desempleo",
  CAPITAL: "Capital",
};

export const hierarchyBlockersLabel = (blockers) =>
  blockers.map((item) => COMPONENT_LABELS[item.component] || item.component).join(", ");

export const OFFER_STATUS_LABELS = {
  PENDING_OFFER: "Oferta pendiente",
  APPLIED: "Aplicada",
  EXPIRED: "Expirada",
  CANCELLED: "Cancelada",
};

export const squareFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 0,
  },
  "& .MuiToggleButtonGroup-grouped": {
    borderRadius: "0 !important",
  },
  "& .MuiToggleButton-root": {
    borderRadius: "0 !important",
  },
};
